'use client';

// @TODO: Refactor this page. It's a mess.
import './sac-controls.scss';

import { createSerializer } from 'nuqs';
import { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { throttle } from 'throttle-debounce';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { Link } from '@sola/ark-core/atoms/link/link.tsx';
import { ChipGroup } from '@sola/ark-core/molecules/chip-group/chip-group.tsx';
import { RadioGroup } from '@sola/ark-core/molecules/radio-group/radio-group.tsx';
import { Select } from '@sola/ark-core/molecules/select/select.tsx';
import { SliderInput } from '@sola/ark-core/molecules/slider-input/slider-input.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { generalConfig } from '@sola/config/lib/getters.ts';
import {
  countryIsoToRegionIso,
  programCountryIsoCodes,
} from '@sola/contentful-model/lib/sac-values.ts';
import { ChildGenderEnum } from '@sola/schema/dotcom/children/childUtilityInterfaces.ts';
import { IconNames } from '@sola/schema/lib/icons/icon-names.ts';
import { SacFilterProps } from '@sola/schema/lib/schema.ts';
import {
  TrackChildSearch,
  trackClick,
} from '@sola/tracking/components/datalayer.tsx';

import {
  SacFiltersEnum,
  SacQueryParams,
  sacQueryParamsSchema,
  useSacQueryParams,
} from '../../utils/query-params.tsx';
import { getBaseSizeProps } from '../../utils/text-utils.tsx';
import { FeatureBase } from '../feature-base/feature-base.tsx';

const { sacMinAge, sacMaxAge } = generalConfig;

type SacControlsProps = SacFilterProps & {
  variant?: 'default' | 'stacked';
  isLive?: boolean;
};

export const generateMonthItems = () =>
  [...Array(12).keys()]
    .map((v) => v + 1)
    .map((v) => {
      return {
        value: v.toString(),
        // TODO: es
        text: Intl.DateTimeFormat('en', { month: 'long' }).format(
          new Date(2000, v - 1),
        ),
      };
    });

export const generateDayItems = () =>
  [...Array(31).keys()]
    .map((v) => v + 1)
    .map((v) => {
      return {
        value: v.toString(),
        text: v.toString(),
      };
    });

const InternalSacControls: React.FC<
  SacControlsProps & {
    sacFilters?: SacQueryParams;
    setSacFilters?: (values: Partial<SacQueryParams>) => void;
  }
> = ({ variant = 'default', isLive, sacFilters, setSacFilters, ...props }) => {
  const { t } = useTranslation(['children', 'core', 'geo']);

  const chips = [
    {
      name: t('children:sac.orphanedChild'),
      value: SacFiltersEnum.enum.orphanedChild,
      icon: 'child' as IconNames,
    },
    {
      name: t('children:sac.hasDisabilities'),
      value: SacFiltersEnum.enum.hasDisabilities,
      icon: 'wheelchair' as IconNames,
    },
    {
      name: t('children:sac.vulnerableToExploitation'),
      value: SacFiltersEnum.enum.vulnerableExploitation,
      icon: 'alert-triangle' as IconNames,
    },
    {
      name: t('children:sac.affectedByHiv'),
      value: SacFiltersEnum.enum.affectedHiv,
      icon: 'virus' as IconNames,
    },
  ];

  const [showAdvanced, setShowAdvanced] = useState<boolean>(
    sacFilters?.gender !== ChildGenderEnum.Either ||
      sacFilters?.ageMin !== undefined ||
      sacFilters?.ageMax !== undefined ||
      (sacFilters?.countries?.length ?? 0) > 0,
  );

  const [ageRange, setAgeRange] = useState<[number, number]>([
    sacFilters?.ageMin ?? sacMinAge,
    sacFilters?.ageMax ?? sacMaxAge,
  ]);

  const [isMultipleCountry, setIsMultipleCountry] = useState(
    sacFilters?.countries && sacFilters?.countries?.length > 1,
  );

  useEffect(() => {
    // our slider component is not a controlled component, so if we reset these values by clearing
    // the url params, we need to reset the age range to the defaults
    const minParam = sacFilters?.ageMin;
    const maxParam = sacFilters?.ageMax;

    if (minParam === sacMinAge && maxParam === sacMaxAge) {
      setAgeRange([sacMinAge, sacMaxAge]);
    }
  }, [sacFilters?.ageMin, sacFilters?.ageMax]);

  const getGenderOptions = () => [
    {
      value: ChildGenderEnum.Male,
      text: t('children:sac.controls.boy'),
    },
    {
      value: ChildGenderEnum.Female,
      text: t('children:sac.controls.girl'),
    },
    { value: ChildGenderEnum.Either, text: t('children:sac.controls.either') },
  ];

  const getCountries = () => [
    { value: '', text: t('children:sac.controls.allCountries') },
    ...(isMultipleCountry
      ? [
          {
            value: 'multiple',
            text: t('children:sac.controls.multipleCountries'),
            disabled: true,
          },
        ]
      : []),
    ...programCountryIsoCodes
      .map((c) => {
        return {
          value: c,
          text: t(`geo:countries.${c}.nameS` as 'geo:countries.BD.nameS'),
        };
      })
      .sort((a, b) => a.text.localeCompare(b.text)),
  ];

  const getMonths = () => [
    { value: '0', text: t('children:sac.controls.month') },
    ...generateMonthItems(),
  ];

  const getDays = () => [
    { value: '0', text: t('children:sac.controls.day') },
    ...generateDayItems(),
  ];

  const throttledSliderHandler = throttle(
    300,
    (minAge: number, maxAge: number) => {
      setSacFilters?.({
        ageMin: minAge,
        ageMax: maxAge,
      });
    },
    { noLeading: true },
  );

  const getSacControls = (className?: string) => (
    <div
      className={`SacControls__controls-container p-comfy-xl grid grid-cols-1 md:grid-cols-3 gap-comfy-lg ${className}`}
    >
      <span>
        {/* eslint-disable-next-line jsx-a11y-x/label-has-associated-control */}
        <label id="gender-group-label" className="form-label label">
          {t('children:sac.wantToSponsor')}
        </label>
        <RadioGroup
          defaultValue={sacFilters?.gender}
          aria-label={t('children:sac.controls.chooseGender')}
          loop={false}
          variant="buttons"
          name="gender"
          aria-labelledby="gender-group-label"
          onChange={(v) => {
            setSacFilters?.({ gender: v?.target?.value });
          }}
          items={getGenderOptions()}
        />
      </span>
      <span>
        <SliderInput
          value={ageRange}
          label={t('children:sac.betweenTheAges')}
          min={sacMinAge}
          max={sacMaxAge}
          name="age"
          showThumbLabels
          onChange={({ newValue }) => {
            const [minAgeValue, maxAgeValue] = newValue;
            setAgeRange([minAgeValue, maxAgeValue]);
          }}
          onCommit={({ newValue }) => {
            if (newValue?.length === 2) {
              const [minAgeValue, maxAgeValue] = newValue;
              setAgeRange([minAgeValue, maxAgeValue]);
              throttledSliderHandler(minAgeValue, maxAgeValue);
            }
          }}
        />
      </span>
      <span>
        <Select
          value={isMultipleCountry ? 'multiple' : sacFilters?.countries}
          label={t('children:sac.whoLivesIn')}
          name="country"
          onChange={(v) => {
            setIsMultipleCountry(false);
            setSacFilters?.({
              countries: [v?.target?.value].filter(Boolean),
            });
          }}
          items={getCountries()}
        />
      </span>
    </div>
  );
  // TODO: Add a button to reset the date
  const getBirthdayControls = () => (
    <div className="flex flex-col md:flex-row items-center justify-center p-comfy-lg gap-comfy-sm">
      <ArkIcon
        iconName="gift-01"
        className="SacControls__birthday-icon hidden md:block"
      />
      <div className="label-md label-moderate">
        {t('children:sac.birthdaySearch')}
      </div>
      <span className="flex gap-comfy-sm">
        <Select
          value={sacFilters?.birthMonth}
          name="birth month"
          aria-label={t('children:sac.controls.month')}
          onChange={(v) => {
            setSacFilters?.({
              birthMonth: Number(v?.target?.value),
            });
          }}
          items={getMonths()}
        />
        <Select
          value={sacFilters?.birthDay}
          name="birth day"
          aria-label={t('children:sac.controls.day')}
          onChange={(v) => {
            setSacFilters?.({
              birthDay: Number(v?.target?.value),
            });
          }}
          items={getDays()}
        />
      </span>
    </div>
  );

  const getAdvancedOptions = () => (
    <div className="SacControls__adv-search-container block md:hidden flex flex-col p-comfy-xs md:p-comfy-sm justify-center">
      {showAdvanced && getSacControls()}
      <ButtonStyle
        variant="tertiary"
        fullWidth
        iconAfter={
          showAdvanced ? (
            <ArkIcon iconName="chevron-up" />
          ) : (
            <ArkIcon iconName="chevron-down" />
          )
        }
      >
        <button
          type="button"
          onClick={() => {
            trackClick(t('children:sac.advancedSearch'));
            setShowAdvanced((v) => !v);
          }}
        >
          {t('children:sac.advancedSearch')}
        </button>
      </ButtonStyle>
    </div>
  );

  const showChips = variant !== 'stacked';

  const getSerializedSacFilters = createSerializer(sacQueryParamsSchema);
  const serializedSacFilters = getSerializedSacFilters(sacFilters ?? {});

  return (
    // TODO: this layout isn't exactly right as the spacing is a bit off
    <div
      className={cn(
        `SacControls SacControls--${variant} flex flex-col gap-roomy-xs`,
      )}
      aria-label={t('children:sac.controls.legend')}
      role="group"
    >
      <TrackChildSearch
        event={{
          gender: sacFilters?.gender || undefined,
          ageMin: sacFilters?.ageMin || undefined,
          ageMax: sacFilters?.ageMax || undefined,
          region: countryIsoToRegionIso(sacFilters?.countries?.[0] || ''),
          country: Array.from(sacFilters?.countries || []),
          birthMonth: sacFilters?.birthMonth || undefined,
          birthDay: sacFilters?.birthDay || undefined,
          orphan:
            sacFilters?.filters?.some(
              (f: string) => f === SacFiltersEnum.enum.orphanedChild,
            ) || undefined,
          disability:
            sacFilters?.filters?.some(
              (f: string) => f === SacFiltersEnum.enum.hasDisabilities,
            ) || undefined,
          highlyVulnerable:
            sacFilters?.filters?.some(
              (f: string) => f === SacFiltersEnum.enum.vulnerableExploitation,
            ) || undefined,
          hiv:
            sacFilters?.filters?.some(
              (f: string) => f === SacFiltersEnum.enum.affectedHiv,
            ) || undefined,
        }}
      />
      <div className="SacControls__card flex flex-col">
        {variant !== 'stacked' && (
          <>
            {getSacControls('hidden md:grid')}
            {getBirthdayControls()}
            {getAdvancedOptions()}
          </>
        )}
        {variant === 'stacked' && (
          <>
            <FeatureBase
              {...props}
              {...getBaseSizeProps(2)}
              {...{
                primaryCTA: undefined,
                secondaryCTA: undefined,
                tertiaryCTA: undefined,
              }}
              className="p-comfy-xl pb-none"
              inline
            />
            {getSacControls('flex flex-col')}
          </>
        )}
        {!isLive && (
          <div className="p-comfy-xl">
            <ButtonStyle variant="primary">
              <Link href={`/sponsor-a-child${serializedSacFilters}`}>
                {t('core:actions.sponsorAChild')}
              </Link>
            </ButtonStyle>
          </div>
        )}
      </div>
      {showChips && (
        <ChipGroup
          chips={chips}
          selectedValues={sacFilters?.filters}
          handleSelectionChange={(v) => {
            setSacFilters?.({
              filters: v as SacFiltersEnum[],
            });
          }}
          isScrollable
        />
      )}
    </div>
  );
};

export const LiveSacControls: React.FC<SacControlsProps> = (props) => {
  const { isLive } = props;
  const { sacFilters, setSacFilters } = useSacQueryParams({
    isLive,
  });
  return (
    <InternalSacControls
      {...props}
      sacFilters={sacFilters}
      setSacFilters={setSacFilters}
    />
  );
};

// During SSG we render the controls without caring about search params,
// so we can deliver the block quickly. Then, when Suspense finishes,
// we update it with the search params on the client.
export const SacControls: React.FC<SacControlsProps> = (props) => {
  return (
    <Suspense fallback={<InternalSacControls {...props} />}>
      <LiveSacControls {...props} />
    </Suspense>
  );
};
